"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { GateAssignmentService } from "../../../Service/TicketingService"
import type { GateAssignment, AccountPerson, GateOption } from "./types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: GateAssignment | null
}

const GateAssignmentModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData }) => {
  const [form, setForm] = useState({
    user: 0,
    gate: "",
    is_active: true,
  })

  const [accounts, setAccounts] = useState<AccountPerson[]>([])
  const [gateOptions] = useState<GateOption[]>([
    { value: "entry_gate", label: "Entry Gate" },
    { value: "exit_gate", label: "Exit Gate" },
    { value: "main_gate", label: "Main Gate" },
    { value: "service_gate", label: "Service Gate" },
    { value: "vip_gate", label: "VIP Gate" },
  ])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (isOpen) {
      fetchAccounts()
    }
  }, [isOpen])

  useEffect(() => {
    if (initialData) {
      setForm({
        user: initialData.user,
        gate: initialData.gate,
        is_active: initialData.is_active,
      })
    } else {
      setForm({
        user: 0,
        gate: "",
        is_active: true,
      })
    }
    setErrors({})
  }, [initialData, isOpen])

  const fetchAccounts = async () => {
    try {
      setLoadingData(true)
      const accountsData = await GateAssignmentService.getAccountsForAssignment()
      setAccounts(accountsData)
    } catch (error) {
      console.error("Failed to fetch accounts:", error)
      toast.error("Failed to load accounts")
    } finally {
      setLoadingData(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setForm({
      ...form,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : name === "user" ? Number(value) : value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!form.user) {
      newErrors.user = "User is required"
    }

    if (!form.gate) {
      newErrors.gate = "Gate is required"
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      if (initialData) {
        await GateAssignmentService.update(initialData.id, form)
        await Swal.fire("Updated!", "Gate assignment updated successfully.", "success")
      } else {
        await GateAssignmentService.create(form)
        await Swal.fire("Created!", "Gate assignment created successfully.", "success")
      }
      onSuccess()
      toggle()
    } catch (error: any) {
      console.error("Failed to save gate assignment:", error)
      if (error.response?.data?.non_field_errors) {
        await Swal.fire("Error", error.response.data.non_field_errors[0], "error")
      } else {
        await Swal.fire("Error", "Something went wrong. Please try again.", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  if (loadingData) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="md">
        <ModalHeader toggle={toggle}>{initialData ? "Edit" : "New"} Gate Assignment</ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </ModalBody>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "New"} Gate Assignment</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="user">User *</Label>
          <Input type="select" name="user" value={form.user || ""} onChange={handleChange} invalid={!!errors.user}>
            <option value="">-- Select User --</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.person_name}
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.user}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="gate">Gate *</Label>
          <Input type="select" name="gate" value={form.gate} onChange={handleChange} invalid={!!errors.gate}>
            <option value="">-- Select Gate --</option>
            {gateOptions.map((gate) => (
              <option key={gate.value} value={gate.value}>
                {gate.label}
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.gate}</FormFeedback>
        </FormGroup>

        <FormGroup check>
          <Input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
          <Label check>Active Assignment</Label>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Create"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default GateAssignmentModal
